import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import useSearchParams from "@/hooks/useSearchParams";
import { Dialog, DialogTitle } from "@bookips/sds";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { patchOrder } from "src/api/booksOrder";
import useSnackbar from "src/store/useSnackbar";
import { Funnel, Step } from "../../Funnel";
import { QueryKeys, useAddressManagement } from "../queries/useAddressManagement";
import { AddressManagementAdd } from "./AddressManagementAdd";
import { AddressManagementList } from "./AddressManagementList";
export const AddressManagementDialog = (param)=>{
    let { open , onClose , listData  } = param;
    var _listData_find;
    const { addAddressAsync , updateAddressAsync , deleteAddressAsync , isAddAddressLoading , isUpdateAddressLoading , isDeleteAddressLoading , putDefaultAddressAsync , isPutDefaultAddressLoading  } = useAddressManagement();
    const queryClient = useQueryClient();
    var _listData_find_id;
    // 기본 배송지라는 기능은 따로 없기 때문에, 일단 배송지 데이터의 첫번째로 선택된 배송지를 기본 배송지로 설정
    const defaultAddressId = (_listData_find_id = (_listData_find = listData === null || listData === void 0 ? void 0 : listData.find((address)=>address.isDefault)) === null || _listData_find === void 0 ? void 0 : _listData_find.id) !== null && _listData_find_id !== void 0 ? _listData_find_id : "";
    const [currentStep, setCurrentStep] = useState("list");
    const [selectedAddress, setSelectedAddress] = useState("");
    const { addSnackbar  } = useSnackbar();
    const updateOrderMutation = useMutation(patchOrder);
    const searchParams = useSearchParams();
    const { setValue , getValues , clearErrors  } = useFormContext();
    const getTitle = ()=>{
        const titles = {
            list: "배송지 선택",
            add: "배송지 추가",
            edit: "배송지 수정"
        };
        return titles[currentStep];
    };
    const handleEditClick = (address)=>{
        setSelectedAddress(address);
        setCurrentStep("edit");
    };
    const handleBackToListAndClose = ()=>{
        setCurrentStep("list");
        onClose();
    };
    const handleBackToList = ()=>{
        setCurrentStep("list");
    };
    const handleAddClick = ()=>{
        if ((listData === null || listData === void 0 ? void 0 : listData.length) === 10) {
            addSnackbar({
                message: "최대 10개까지 만들 수 있어요.",
                variant: "error"
            });
        } else {
            setCurrentStep("add");
        }
    };
    const handleAddressSave = async (address)=>{
        try {
            await addAddressAsync(address);
            queryClient.invalidateQueries({
                queryKey: [
                    QueryKeys.ADDRESS_LIST
                ]
            });
            handleBackToList();
            addSnackbar({
                message: "배송지가 추가됐어요.",
                variant: "success"
            });
        } catch (error) {
            ;
            addSnackbar({
                message: "다시 시도해주세요.",
                variant: "error"
            });
        }
    };
    const handleAddressUpdate = async (address)=>{
        try {
            await updateAddressAsync({
                id: selectedAddress,
                ...address
            });
            queryClient.invalidateQueries({
                queryKey: [
                    QueryKeys.ADDRESS_LIST
                ]
            });
            handleBackToList();
            addSnackbar({
                message: "배송지가 수정됐어요.",
                variant: "success"
            });
        } catch (error) {
            ;
            addSnackbar({
                message: "다시 시도해주세요.",
                variant: "error"
            });
        }
    };
    const handleAddressDelete = async (id)=>{
        try {
            await deleteAddressAsync(id);
            queryClient.invalidateQueries({
                queryKey: [
                    QueryKeys.ADDRESS_LIST
                ]
            });
            handleBackToList();
            addSnackbar({
                message: "배송지가 삭제됐어요.",
                variant: "error"
            });
        } catch (error) {
            ;
            addSnackbar({
                message: "다시 시도해주세요.",
                variant: "error"
            });
        }
    };
    // 주소 수정을 할 경우, 배송비 재계산을 위해서 order를 업데이트 합니다.
    const handleAddressChange = async (id)=>{
        try {
            await putDefaultAddressAsync(id);
            const response = await updateOrderMutation.mutateAsync({
                id: searchParams.get("id"),
                addressId: id
            });
            clearErrors([
                "address1",
                "address2",
                "recipientName",
                "recipientPhone"
            ]);
            // 다른 컴포넌트에서도 업데이트된 값을 반영하기 위해 쿼리 무효화
            // updateInitialData에서 쿠폰 상태를 자동으로 보존함
            const orderType = searchParams.get("orderType");
            const orderId = searchParams.get("id");
            queryClient.invalidateQueries({
                queryKey: [
                    "payment",
                    orderType,
                    orderId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    QueryKeys.ADDRESS_LIST
                ]
            });
            onClose();
        } catch (error) {
            ;
        }
    };
    const checkFormState = ()=>{
        if ((listData === null || listData === void 0 ? void 0 : listData.length) === 0) {
            setValue("addressName", "");
            setValue("recipientName", "");
            setValue("phone", "");
            setValue("address1", "");
            setValue("address2", "");
            setValue("postalCode", "");
            setValue("message", "");
        }
    };
    useEffect(()=>{
        if ((listData === null || listData === void 0 ? void 0 : listData.length) === 0) {
            checkFormState();
        }
    }, [
        listData
    ]);
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        classes: {
            contentWrapper: "max-sm:w-[calc(100vw-40px)] overflow-auto max-h-screen",
            content: "max-sm:px-5"
        },
        onOpenChange: ()=>handleBackToListAndClose(),
        title: /*#__PURE__*/ _jsx(DialogTitle, {
            title: getTitle(),
            className: "px-7 pt-7 max-sm:px-5 max-sm:pt-5"
        }),
        children: /*#__PURE__*/ _jsxs(Funnel, {
            step: currentStep,
            children: [
                /*#__PURE__*/ _jsx(Step, {
                    name: "list",
                    children: /*#__PURE__*/ _jsx(AddressManagementList, {
                        listData: listData,
                        defaultAddressId: defaultAddressId,
                        onEditClick: handleEditClick,
                        onDeleteClick: handleAddressDelete,
                        onClose: onClose,
                        handleAddStepClick: handleAddClick,
                        onChangeAddress: handleAddressChange
                    })
                }),
                /*#__PURE__*/ _jsx(Step, {
                    name: "add",
                    children: /*#__PURE__*/ _jsx(AddressManagementAdd, {
                        onAddressSave: handleAddressSave,
                        onClose: ()=>{
                            if (defaultAddressId !== "") {
                                handleBackToListAndClose();
                            } else {
                                onClose();
                            }
                        },
                        isLoading: isAddAddressLoading
                    })
                }),
                /*#__PURE__*/ _jsx(Step, {
                    name: "edit",
                    children: /*#__PURE__*/ _jsx(AddressManagementAdd, {
                        addressData: listData === null || listData === void 0 ? void 0 : listData.find((addr)=>addr.id === selectedAddress),
                        onAddressSave: handleAddressUpdate,
                        onClose: ()=>{
                            handleBackToListAndClose();
                        },
                        isLoading: isUpdateAddressLoading
                    })
                })
            ]
        })
    });
};
